/**
 * What the old kiosk's summary and ticket cards read of a journey (TripSummary, TripTicket), and the few words they
 * compute from it. The builders fill it from the session (builders.ts `journeyOf`); the cards only format.
 */
import { z } from 'zod';
import type { Translate } from '@platform/i18n/translator';

export const seatSchema = z.object({ seat: z.string(), coach: z.string() });

export const legSchema = z.object({
  trainType: z.string(),
  trainNumber: z.string(),
  departure: z.string(),
  arrival: z.string(),
  origin: z.string(),
  destination: z.string(),
});
export type Leg = z.infer<typeof legSchema>;

export const extrasSchema = z.object({
  petsSmall: z.number().default(0),
  petsBig: z.number().default(0),
  bikesNormal: z.number().default(0),
  bikesFoldable: z.number().default(0),
});
export type Extras = z.infer<typeof extrasSchema>;

export const journeySchema = z.object({
  trainType: z.string(),
  trainNumber: z.string(),
  departure: z.string(),
  arrival: z.string(),
  origin: z.string(),
  destination: z.string(),
  durationMin: z.number(),
  changes: z.number().default(0),
  legs: z.array(legSchema).default([]),
  travelClass: z.string().nullable(),
  seats: z.array(seatSchema).default([]),
  adults: z.number(),
  children: z.number().default(0),
  extras: extrasSchema,
  /** The train's services on board, by the operator's ids (TripSummary draws the ones it knows). */
  onBoard: z.array(z.string()).default([]),
  /** The train's position on the results (show_detail's `idx`): its itinerary opens from the summary and the ticket too. */
  idx: z.number().nullable().default(null),
});
export type Journey = z.infer<typeof journeySchema>;

/** The icon of each service on board an operator can name (operators/<id>/operator.json). */
const ON_BOARD_ICONS: Record<string, string> = { luggage: 'luggage', power: 'plug', wifi: 'wifi', bar: 'food', air_conditioning: 'cooling' };

/** The icons of a train's services on board, in its order; a service without an icon is not drawn. */
export function onBoardIcons(services: string[]): string[] {
  return services.flatMap((id) => ON_BOARD_ICONS[id] ?? []);
}

/** «Classe Prima Business» → «Prima Business»: the chip and the ticket say «Classe» themselves. */
export function classOnly(display: string): string {
  return display.replace(/\s*\bclass(?:e)?\b\s*/i, ' ').trim();
}

/** The old kiosk's duration: «3h 5min», «45min», «2h». */
export function classicDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}min`;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

export function changesLabel(changes: number, t: Translate): string {
  if (changes === 0) return t('trains.summary.changes_none');
  return changes === 1 ? t('trains.summary.changes_one') : t('trains.summary.changes_many', { n: changes });
}

export function paxLabel(pax: number, t: Translate): string {
  return pax === 1 ? t('trains.summary.passenger_one') : t('trains.summary.passengers_many', { n: pax });
}

const EXTRA_WORDS: Record<keyof Extras, { word: string; icon: string }> = {
  petsSmall: { word: 'trains.passengers.pets_small', icon: 'dog' },
  petsBig: { word: 'trains.passengers.pets_big', icon: 'dog' },
  bikesNormal: { word: 'trains.passengers.bikes', icon: 'bike' },
  bikesFoldable: { word: 'trains.passengers.bikes_foldable', icon: 'bike' },
};

/** One chip per extra booked: «2 cani piccoli». */
export function extraChips(extras: Extras, t: Translate): { key: string; icon: string; label: string }[] {
  return (Object.keys(EXTRA_WORDS) as (keyof Extras)[])
    .filter((k) => extras[k] > 0)
    .map((k) => ({ key: k, icon: EXTRA_WORDS[k].icon, label: `${extras[k]} ${t(EXTRA_WORDS[k].word)}` }));
}
